<template>
  <div v-if="productImages.length" class="product-gallery">
    <!-- Main image -->
    <div class="gallery-main" @click="onPreview(productImages[activeIndex])">
      <transition name="gallery-fade" mode="out-in">
        <img :key="activeIndex" :src="productImages[activeIndex]" alt="Product image" class="gallery-main__img" />
      </transition>
      <div class="gallery-main__overlay">
        <v-icon color="white" size="18">mdi-magnify-plus-outline</v-icon>
      </div>
    </div>

    <!-- Thumbnail strip (bottom) -->
    <div v-if="productImages.length > 1" class="gallery-thumbs">
      <button
        v-for="(image, idx) in productImages"
        :key="idx"
        class="gallery-thumb"
        :class="{ 'is-active': activeIndex === idx }"
        @click="activeIndex = idx"
      >
        <img :src="image" :alt="`View ${idx + 1}`" />
      </button>
    </div>

    <PreviewImage v-if="showPreview" :src="currentImage" :showPreview="showPreview" @close="closePreviewImage" />
  </div>
</template>

<script>
import imageHelpers from '@/helpers/image';
import PreviewImage from '@/components/chatbot/PreviewImage.vue';

export default {
  props: ['images'],
  components: { PreviewImage },
  data() {
    return {
      activeIndex: 0,
      showPreview: false,
      currentImage: null,
    };
  },
  computed: {
    productImages() {
      return this.images && this.images.length
        ? this.images.map(image => imageHelpers.url(image.src, { width: 400, height: 400 }))
        : [];
    },
  },
  watch: {
    images() {
      this.activeIndex = 0;
    },
  },
  methods: {
    onPreview(image) {
      this.currentImage = image;
      this.showPreview = true;
    },
    closePreviewImage() {
      this.showPreview = false;
      this.currentImage = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.product-gallery {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gallery-thumbs {
  display: flex;
  flex-direction: row;
  gap: 5px;
  overflow-x: auto;
  padding-bottom: 2px;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #d0d1d3;
    border-radius: 2px;
  }
}

.gallery-thumb {
  flex: 0 0 54px;
  height: 54px;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid transparent;
  background: var(--v-background-base);
  cursor: pointer;
  padding: 0;
  outline: none;
  flex-shrink: 0;
  transition: border-color 0.15s ease;

  &.is-active {
    border-color: var(--v-primary-base);
  }

  &:hover:not(.is-active) {
    border-color: var(--v-border-base);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.gallery-main {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid var(--v-border-base);
  aspect-ratio: 1;

  &__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  &__overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0);
    transition: background 0.2s ease;

    .v-icon {
      opacity: 0;
      transition: opacity 0.2s ease;
    }
  }

  &:hover &__overlay {
    background: rgba(0, 0, 0, 0.18);

    .v-icon {
      opacity: 1;
    }
  }
}

.gallery-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  aspect-ratio: 1;
  background: #f9fafb;
  border: 1px dashed var(--v-border-base);
  border-radius: 10px;
  color: #9ca3af;

  span {
    font-size: 12px;
  }
}

.gallery-fade-enter-active,
.gallery-fade-leave-active {
  transition: opacity 0.1s ease;
}
.gallery-fade-enter,
.gallery-fade-leave-to {
  opacity: 0;
}
</style>
